import { z } from 'zod';

/**
 * Keys of the `rule_tables` compliance data (build-packet §B7). The Zod enum is the
 * single source of truth; `RULE_KEYS` and `RuleKey` are derived from it.
 */
export const ruleKeySchema = z.enum([
  'deposit_multiplier_default',
  'deposit_multiplier_servicemember',
  'deposit_return_days',
  'deposit_repair_final_days',
  'screening_fee_cap',
  'entry_notice_hours',
  'rent_increase_notice_days',
  'ab1482_cap',
  'ab2747_applies',
]);
export type RuleKey = z.infer<typeof ruleKeySchema>;
export const RULE_KEYS = ruleKeySchema.options;

/** An ISO-8601 date or date-time string (validated via Date.parse — runtime-agnostic). */
export const isoDateString = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), {
    message: 'must be an ISO-8601 date or date-time',
  });

/**
 * A STRICT ISO-8601 date-TIME with an explicit offset (`Z` or `±HH:MM`) — review FIX 1.
 * `isoDateString` above accepts an offset-less datetime (e.g. `2026-07-14T00:00:00`), which
 * `Date.parse` resolves in the HOST's local timezone — the same input string means a different
 * instant on a UTC vs. a non-UTC runtime (this app runs on an America/Phoenix box). For a
 * date-only field that ambiguity doesn't matter (a bare `YYYY-MM-DD` has no time-of-day to be
 * ambiguous about), so those fields stay on `isoDateString`; but a field whose VALUE is compared
 * against "now" for a compliance deadline (the §1954 entry-notice lead time, the rendered notice
 * text, the registered deadline's `due_at`) must resolve to the same instant everywhere, so it
 * requires an explicit offset and REJECTS an offset-less datetime.
 */
export const isoDateTimeStrict = z.string().datetime({
  offset: true,
  message: 'must be an ISO-8601 datetime with an explicit Z or ±HH:MM offset',
});

/** Query params for GET /api/rules/:key */
export const getRuleQuerySchema = z.object({
  asOf: isoDateString.optional(),
});
export type GetRuleQuery = z.infer<typeof getRuleQuerySchema>;

/** Jurisdiction codes. SLO is the only one seeded in Phase 1. */
export const jurisdictionSchema = z.enum(['SLO']);
export type Jurisdiction = z.infer<typeof jurisdictionSchema>;

/* ------------------------------------------------------------------ *
 * EPIC 1 — Auth & RBAC edge schemas.                                 *
 * ------------------------------------------------------------------ */

/**
 * RBAC role. `contractor` (0.25.0) = a maintenance vendor limited to the jobs assigned to them
 * (`/api/contractor/*`); every other route names the roles it admits, so it is denied by default.
 */
export const roleSchema = z.enum(['owner', 'staff', 'tenant', 'contractor']);
export type Role = z.infer<typeof roleSchema>;

/** Login surface — separate admin vs. tenant vs. contractor sign-in (T1.1; contractor 0.25.0). */
export const surfaceSchema = z.enum(['admin', 'tenant', 'contractor']);
export type Surface = z.infer<typeof surfaceSchema>;

/** Email, trimmed + lowercased so it matches the stored (org,email) unique key. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email({ message: 'must be a valid email' });

/** Password policy at the edge (≥12 chars; hashing floor is enforced server-side). */
export const passwordSchema = z
  .string()
  .min(12, { message: 'must be at least 12 characters' })
  .max(200, { message: 'must be at most 200 characters' });

/** A 6-digit TOTP code or an alphanumeric recovery code. */
export const mfaCodeSchema = z
  .string()
  .trim()
  .min(6, { message: 'code is required' })
  .max(20, { message: 'code is too long' });

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, { message: 'password is required' }).max(200),
  surface: surfaceSchema,
});
export type LoginInput = z.infer<typeof loginSchema>;

export const magicLinkRequestSchema = z.object({
  email: emailSchema,
  surface: surfaceSchema,
});
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestSchema>;

export const magicLinkVerifySchema = z.object({
  token: z.string().min(1, { message: 'token is required' }),
});
export type MagicLinkVerifyInput = z.infer<typeof magicLinkVerifySchema>;

export const mfaConfirmSchema = z.object({ code: mfaCodeSchema });
export type MfaConfirmInput = z.infer<typeof mfaConfirmSchema>;

export const mfaVerifySchema = z.object({ code: mfaCodeSchema });
export type MfaVerifyInput = z.infer<typeof mfaVerifySchema>;

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1, { message: 'current password is required' }).max(200),
  newPassword: passwordSchema,
});
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;

/* ------------------------------------------------------------------ *
 * Staff / admin account management (owner-only).                     *
 * ------------------------------------------------------------------ */

/** Admin roles only — a staff/owner login account is never a tenant. */
export const staffRoleSchema = z.enum(['owner', 'staff']);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export const staffCreateSchema = z.object({
  email: emailSchema,
  name: z.string().trim().min(1, { message: 'name is required' }).max(200),
  role: staffRoleSchema,
});
export type StaffCreateInput = z.infer<typeof staffCreateSchema>;

export const staffUpdateSchema = z.object({
  role: staffRoleSchema.optional(),
  status: z.enum(['active', 'suspended']).optional(),
});
export type StaffUpdateInput = z.infer<typeof staffUpdateSchema>;

/* ------------------------------------------------------------------ *
 * EPIC 2 — Compliance spine edge schemas (deadline engine).          *
 * ------------------------------------------------------------------ */

/** The canonical deadline clock types (blueprint §iv / resolution #10). */
export const clockTypeSchema = z.enum([
  'deposit_return',
  'entry_notice',
  'rent_increase_notice',
  'repair_final',
]);
export type ClockType = z.infer<typeof clockTypeSchema>;

/**
 * Manual/backfill deadline registration (T2.3). The producing flows land in later epics; in
 * Phase-1 EPIC-2 this admin endpoint registers a clock directly. `entryStartAt` is required
 * for `entry_notice` (its due date is the scheduled entry itself); `increasePct` selects the
 * 30- vs 90-day window for `rent_increase_notice`.
 */
export const deadlineRegisterSchema = z
  .object({
    clockType: clockTypeSchema,
    subjectType: z.string().trim().min(1).max(64),
    subjectId: z.string().trim().min(1).max(128),
    startEvent: z.string().trim().min(1).max(128),
    startAt: isoDateString,
    /** Required for entry_notice — the scheduled entry moment (the due date). */
    entryStartAt: isoDateString.optional(),
    /** Required for rent_increase_notice — picks the 30 (≤10%) vs 90 (>10%) day window. */
    increasePct: z.number().min(0).max(1000).optional(),
  })
  .refine((v) => v.clockType !== 'entry_notice' || v.entryStartAt != null, {
    message: 'entryStartAt is required for entry_notice',
    path: ['entryStartAt'],
  })
  .refine((v) => v.clockType !== 'rent_increase_notice' || v.increasePct != null, {
    message: 'increasePct is required for rent_increase_notice',
    path: ['increasePct'],
  });
export type DeadlineRegisterInput = z.infer<typeof deadlineRegisterSchema>;

/* ------------------------------------------------------------------ *
 * EPIC 3 — Portfolio edge schemas (properties / units / appliances). *
 * ------------------------------------------------------------------ */

/** A non-negative integer count of cents / whole units. */
const nonNegInt = z.number().int().min(0);
/** An optional non-negative integer (nullable → coalesced to undefined on the wire). */
const optNonNegInt = nonNegInt.nullish();

export const propertyTypeSchema = z.enum([
  'single_family',
  'multi_family',
  'condo',
  'townhouse',
  'adu',
]);
export type PropertyType = z.infer<typeof propertyTypeSchema>;

export const cityRegistrationStatusSchema = z.enum([
  'not_required',
  'pending',
  'registered',
  'exempt',
]);
export type CityRegistrationStatus = z.infer<typeof cityRegistrationStatusSchema>;

export const unitStatusSchema = z.enum(['vacant', 'occupied', 'make_ready', 'off_market']);
export type UnitStatus = z.infer<typeof unitStatusSchema>;

export const ab1482StatusSchema = z.enum(['exempt', 'covered', 'unknown']);
export type Ab1482Status = z.infer<typeof ab1482StatusSchema>;

export const applianceCategorySchema = z.enum([
  'stove',
  'refrigerator',
  'dishwasher',
  'washer',
  'dryer',
  'microwave',
  'water_heater',
  'hvac',
  'disposal',
  'other',
]);
export type ApplianceCategory = z.infer<typeof applianceCategorySchema>;

export const applianceConditionSchema = z.enum([
  'working',
  'needs_repair',
  'not_working',
  'not_present',
]);
export type ApplianceCondition = z.infer<typeof applianceConditionSchema>;

const shortText = z.string().trim().min(1).max(200);
const optShortText = z.string().trim().max(200).nullish();

/** Create a property. `jurisdiction` defaults to SLO (the only Phase-1 rule set). */
export const propertyCreateSchema = z.object({
  ownerId: z.string().trim().min(1).max(128),
  name: shortText,
  line1: shortText,
  line2: optShortText,
  city: shortText.default('San Luis Obispo'),
  state: z.string().trim().min(1).max(64).default('CA'),
  postalCode: z.string().trim().max(16).nullish(),
  jurisdiction: jurisdictionSchema.default('SLO'),
  yearBuilt: z.number().int().min(1800).max(2200).nullish(),
  type: propertyTypeSchema.default('single_family'),
  cityRegistrationStatus: cityRegistrationStatusSchema.default('not_required'),
  cityRegistrationId: optShortText,
  cityRegistrationDate: isoDateString.nullish(),
});
export type PropertyCreateInput = z.infer<typeof propertyCreateSchema>;

/** Update a property. All fields optional; `ownerId` is NOT re-assignable here. */
export const propertyUpdateSchema = z
  .object({
    name: shortText.optional(),
    line1: shortText.optional(),
    line2: optShortText,
    city: shortText.optional(),
    state: z.string().trim().min(1).max(64).optional(),
    postalCode: z.string().trim().max(16).nullish(),
    jurisdiction: jurisdictionSchema.optional(),
    yearBuilt: z.number().int().min(1800).max(2200).nullish(),
    type: propertyTypeSchema.optional(),
    cityRegistrationStatus: cityRegistrationStatusSchema.optional(),
    cityRegistrationId: optShortText,
    cityRegistrationDate: isoDateString.nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type PropertyUpdateInput = z.infer<typeof propertyUpdateSchema>;

/** An appliance nested in a unit create/update (no id — replace-the-set semantics). */
export const applianceInputSchema = z.object({
  category: applianceCategorySchema,
  make: optShortText,
  model: optShortText,
  serial: optShortText,
  condition: applianceConditionSchema.default('working'),
  isHabitabilityRequired: z.boolean().default(false),
  installedOn: isoDateString.nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type ApplianceInput = z.infer<typeof applianceInputSchema>;

/** Standalone appliance create (managed under a unit). */
export const applianceCreateSchema = applianceInputSchema;
export type ApplianceCreateInput = z.infer<typeof applianceCreateSchema>;
/** Standalone appliance update — all optional. */
export const applianceUpdateSchema = z
  .object({
    category: applianceCategorySchema.optional(),
    make: optShortText,
    model: optShortText,
    serial: optShortText,
    condition: applianceConditionSchema.optional(),
    isHabitabilityRequired: z.boolean().optional(),
    installedOn: isoDateString.nullish(),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type ApplianceUpdateInput = z.infer<typeof applianceUpdateSchema>;

/** Create a unit under a property (the property comes from the route path). */
export const unitCreateSchema = z.object({
  label: shortText,
  beds: nonNegInt.default(0),
  /** Tenths of a bath — e.g. 15 = 1.5 baths (integers-only lean). */
  bathsX10: nonNegInt.default(0),
  sqft: optNonNegInt,
  marketRentCents: optNonNegInt,
  depositCents: optNonNegInt,
  status: unitStatusSchema.default('vacant'),
  ab1482Status: ab1482StatusSchema.default('unknown'),
  exemptionNoticeServed: z.boolean().default(false),
  exemptionNoticeServedDate: isoDateString.nullish(),
  hasSecondUnitAdu: z.boolean().default(false),
  cityRegistrationStatus: cityRegistrationStatusSchema.default('not_required'),
  notes: z.string().trim().max(2000).nullish(),
  /** Optional inventory to seed on create (replace-the-set). */
  appliances: z.array(applianceInputSchema).max(50).optional(),
});
export type UnitCreateInput = z.infer<typeof unitCreateSchema>;

/** Update a unit. All fields optional; appliances (when present) REPLACE the set. */
export const unitUpdateSchema = z
  .object({
    label: shortText.optional(),
    beds: nonNegInt.optional(),
    bathsX10: nonNegInt.optional(),
    sqft: optNonNegInt,
    marketRentCents: optNonNegInt,
    depositCents: optNonNegInt,
    status: unitStatusSchema.optional(),
    ab1482Status: ab1482StatusSchema.optional(),
    exemptionNoticeServed: z.boolean().optional(),
    exemptionNoticeServedDate: isoDateString.nullish(),
    hasSecondUnitAdu: z.boolean().optional(),
    cityRegistrationStatus: cityRegistrationStatusSchema.optional(),
    notes: z.string().trim().max(2000).nullish(),
    appliances: z.array(applianceInputSchema).max(50).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type UnitUpdateInput = z.infer<typeof unitUpdateSchema>;

/* ------------------------------------------------------------------ *
 * EPIC 4 — Tenants & leases edge schemas.                            *
 * ------------------------------------------------------------------ */

const idRef = z.string().trim().min(1).max(128);
const optPhone = z.string().trim().max(40).nullish();
const optNotes = z.string().trim().max(2000).nullish();

/** Create a tenant (owner/staff). `userId` optionally links a portal-login user. */
export const tenantCreateSchema = z.object({
  firstName: shortText,
  lastName: shortText,
  email: emailSchema.nullish(),
  phone: optPhone,
  notes: optNotes,
  /** Optional portal-user link — verified to belong to the org server-side. */
  userId: idRef.nullish(),
});
export type TenantCreateInput = z.infer<typeof tenantCreateSchema>;

/** Update a tenant — all fields optional. `userId: null` unlinks the portal user. */
export const tenantUpdateSchema = z
  .object({
    firstName: shortText.optional(),
    lastName: shortText.optional(),
    email: emailSchema.nullish(),
    phone: optPhone,
    notes: optNotes,
    userId: idRef.nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type TenantUpdateInput = z.infer<typeof tenantUpdateSchema>;

export const leaseStatusSchema = z.enum([
  'draft',
  'pending_move_in',
  'active',
  'ended',
  'terminated',
]);
export type LeaseStatus = z.infer<typeof leaseStatusSchema>;

export const leaseTermTypeSchema = z.enum(['fixed', 'month_to_month']);
export type LeaseTermType = z.infer<typeof leaseTermTypeSchema>;

export const leaseTenantRoleSchema = z.enum(['primary', 'co_tenant', 'guarantor', 'occupant']);
export type LeaseTenantRole = z.infer<typeof leaseTenantRoleSchema>;

export const scheduleCadenceSchema = z.enum(['monthly', 'semi_monthly', 'weekly', 'custom']);
export type ScheduleCadence = z.infer<typeof scheduleCadenceSchema>;

/** A tenant to attach to a lease (per-lease service-member flag lives here — resolution #2). */
export const leaseTenantInputSchema = z.object({
  tenantId: idRef,
  isServiceMember: z.boolean().default(false),
  role: leaseTenantRoleSchema.default('co_tenant'),
});
export type LeaseTenantInput = z.infer<typeof leaseTenantInputSchema>;

/**
 * One schedule installment (T4.3). `amountCents` is OPTIONAL — omit it on every installment
 * to have the server split rent evenly (remainder to the last); supply it on every
 * installment for an explicit split (Σ must equal rent, enforced server-side as 422).
 */
export const installmentInputSchema = z.object({
  dayOfMonth: z.number().int().min(1).max(31),
  amountCents: nonNegInt.optional(),
  label: optShortText,
  sortOrder: z.number().int().min(0).max(64).optional(),
});
export type InstallmentInputSchema = z.infer<typeof installmentInputSchema>;

/** A payment schedule (T4.3) — e.g. 1st & 15th. ≥1 installment (shape); Σ==rent (server). */
export const scheduleInputSchema = z.object({
  cadence: scheduleCadenceSchema.default('monthly'),
  installments: z.array(installmentInputSchema).min(1).max(31),
});
export type ScheduleInput = z.infer<typeof scheduleInputSchema>;

/**
 * Create a lease as a single logical operation (T4.1/T4.2/T4.3). The `tenants` array is
 * deliberately NOT `.min(1)` at the shape layer — the "≥1 tenant" rule is a business
 * invariant enforced in the handler as **422** (not a 400 shape error), same as the
 * Σ==rent and MTM/fixed-endDate rules.
 */
export const leaseCreateSchema = z.object({
  unitId: idRef,
  status: leaseStatusSchema.default('draft'),
  termType: leaseTermTypeSchema.default('fixed'),
  startDate: isoDateString,
  endDate: isoDateString.nullish(),
  rentCents: nonNegInt,
  depositCents: optNonNegInt,
  paymentFrequency: z.string().trim().min(1).max(32).default('monthly'),
  exemptionNoticeInLease: z.boolean().default(false),
  moveInDate: isoDateString.nullish(),
  moveOutDate: isoDateString.nullish(),
  notes: optNotes,
  tenants: z.array(leaseTenantInputSchema).max(20),
  /** Optional — omitted → a single full-rent monthly installment on the 1st. */
  schedule: scheduleInputSchema.optional(),
});
export type LeaseCreateInput = z.infer<typeof leaseCreateSchema>;

/** Update a lease core (owner/staff). Tenants + schedule are managed via their own routes. */
export const leaseUpdateSchema = z
  .object({
    status: leaseStatusSchema.optional(),
    termType: leaseTermTypeSchema.optional(),
    startDate: isoDateString.optional(),
    endDate: isoDateString.nullish(),
    rentCents: nonNegInt.optional(),
    depositCents: optNonNegInt,
    paymentFrequency: z.string().trim().min(1).max(32).optional(),
    exemptionNoticeInLease: z.boolean().optional(),
    moveInDate: isoDateString.nullish(),
    moveOutDate: isoDateString.nullish(),
    notes: optNotes,
    remindersEnabled: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type LeaseUpdateInput = z.infer<typeof leaseUpdateSchema>;

/** Replace a lease's payment schedule (PUT /:id/schedule). Σ==rent enforced server-side. */
export const scheduleReplaceSchema = scheduleInputSchema;
export type ScheduleReplaceInput = z.infer<typeof scheduleReplaceSchema>;

/** Add one tenant to an existing lease (POST /:id/tenants). */
export const leaseTenantAddSchema = leaseTenantInputSchema;
export type LeaseTenantAddInput = z.infer<typeof leaseTenantAddSchema>;

/* ------------------------------------------------------------------ *
 * EPIC 5 — Ledger edge schema (manual entry; §1947.3 refinements).   *
 * ------------------------------------------------------------------ */

/** The five ledger entry types (blueprint §0006). */
export const ledgerEntryTypeSchema = z.enum(['charge', 'payment', 'credit', 'fee', 'refund']);
export type LedgerEntryTypeInput = z.infer<typeof ledgerEntryTypeSchema>;

/**
 * Phase-1 payment methods — NON-EFT only (blueprint resolution #18 / §1947.3). No `ach`/`card`
 * here: the ledger records manual, non-electronic payments only in Phase 1.
 */
export const ledgerMethodSchema = z.enum(['check', 'money_order', 'cashier_check', 'cash']);
export type LedgerMethodInput = z.infer<typeof ledgerMethodSchema>;

/**
 * Post one ledger entry (T5.1/T5.2; owner/staff). `amountCents` is a POSITIVE magnitude (the
 * sign is derived from `type` via `LEDGER_SIGN`). This schema validates SHAPE only (→ 400 on a
 * bad shape). The cross-field §1947.3 rules — a `payment` requires a `method`, a `payment` MUST
 * NOT carry a fee, and a check/money_order/cashier_check requires a non-empty `reference` — are
 * legal BUSINESS invariants, enforced by `checkLedgerPaymentRules` (packages/shared/compliance)
 * as **422** in the handler (the same convention as the lease invariants), NOT as Zod 400s, so a
 * legally-invalid entry is unprocessable rather than merely malformed. `idempotencyKey` is
 * optional — supply it to make a retried post return the SAME entry (the DO dedupes on it).
 */
/**
 * Upper bound on a single ledger money field (cents). Well under 2^53 so the DO's JS running
 * balance never loses integer precision even after many entries, and far above any real
 * rent/deposit amount. Guards against an unbounded amount poisoning the balance math.
 */
const MAX_LEDGER_CENTS = 100_000_000_00; // $100,000,000.00

export const postEntrySchema = z.object({
  type: ledgerEntryTypeSchema,
  /** Positive integer cents (magnitude only; sign derives from `type`). */
  amountCents: z
    .number()
    .int()
    .positive({ message: 'amountCents must be a positive integer' })
    .max(MAX_LEDGER_CENTS, { message: 'amountCents exceeds the maximum' }),
  memo: z.string().trim().max(500).nullish(),
  method: ledgerMethodSchema.nullish(),
  reference: z.string().trim().max(200).nullish(),
  /** Fee in cents (default 0). A payment refuses any fee (§1947.3 — enforced as 422). */
  feeCents: z.number().int().min(0).max(MAX_LEDGER_CENTS, { message: 'feeCents exceeds the maximum' }).default(0),
  /** ISO date the entry takes effect (defaults to now server-side when omitted). */
  effectiveDate: isoDateString.nullish(),
  /** Optional client-supplied idempotency key (the DO dedupes on it). */
  idempotencyKey: z.string().trim().min(1).max(200).optional(),
});
export type PostEntryInput = z.infer<typeof postEntrySchema>;

/* ------------------------------------------------------------------ *
 * EPIC 7 — Documents edge schemas (upload / register / signed-url).  *
 * ------------------------------------------------------------------ */

export const documentSourceTypeSchema = z.enum([
  'upload',
  'lease',
  'receipt',
  'work_order',
  'notice',
  // 0.24.0 — the finalized § 1950.5 statement + move-out photos/receipts uploaded from that flow.
  'move_out',
]);
export type DocumentSourceType = z.infer<typeof documentSourceTypeSchema>;

export const documentRetentionClassSchema = z.enum([
  'lease',
  'financial',
  'inspection_photo',
  'notice',
  'maintenance',
  'identity',
  'general',
  // 0.24.0 — system-of-record legal documents (the finalized deposit statement).
  'legal',
]);
export type DocumentRetentionClass = z.infer<typeof documentRetentionClassSchema>;

export const documentVisibilitySchema = z.enum(['owner_only', 'tenant_visible']);
export type DocumentVisibility = z.infer<typeof documentVisibilitySchema>;

export const ab2801PhaseSchema = z.enum(['move_in', 'move_out', 'post_repair']);
export type Ab2801Phase = z.infer<typeof ab2801PhaseSchema>;

const filenameSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  // Reject control chars + path separators (assessment #15) — a CR/LF would otherwise reach the
  // Content-Disposition header and self-trigger a 500 (or, defensively, header splitting).
  .refine(
    (s) =>
      ![...s].some((ch) => {
        const c = ch.charCodeAt(0);
        return c < 0x20 || c === 0x7f || ch === '/' || ch === '\\';
      }),
    { message: 'filename must not contain control characters or path separators' },
  );
const contentTypeSchema = z
  .string()
  .trim()
  .min(1)
  .max(160)
  // Reject control chars (CR/LF etc.) that would otherwise reach the Content-Type response header
  // on the inline document-download path (header CR/LF). Media-type separators (`/ ; =`) stay legal.
  .refine((s) => ![...s].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f), {
    message: 'contentType must not contain control characters',
  });
/**
 * Static upper bound on a base64-encoded upload body (review FIX 3). Without this, a request
 * body with NO max would be buffered + base64-DECODED into memory in full before the route's
 * env-based `MAX_UPLOAD_BYTES` post-decode check ever runs — a pathologically large body is an
 * OOM risk regardless of the configured cap. Sized to the DEFAULT `MAX_UPLOAD_BYTES` (25 MiB =
 * 26_214_400 bytes) worst case: base64 expands by 4/3, rounded up to the next 4-char group, plus
 * a little slack for whitespace/padding — `ceil(26_214_400 * 4 / 3)` = 34_952_534, +6 slack.
 * The route's env-based post-decode check still enforces the actual (possibly lower configured)
 * limit; this only bounds the pre-decode buffer.
 */
const MAX_CONTENT_BASE64_LENGTH = 34_952_540;
const contentBase64Schema = z
  .string()
  .min(1, { message: 'contentBase64 is required' })
  .max(MAX_CONTENT_BASE64_LENGTH, { message: 'file too large' });
/** Lowercase hex SHA-256 (64 chars) — validated for the register-existing path. */
const sha256HexSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[0-9a-f]{64}$/, { message: 'must be a 64-char lowercase hex sha256' });

/** The shared poly-link + classification fields common to upload + register. */
const documentLinkFields = {
  title: optShortText,
  sourceType: documentSourceTypeSchema.default('upload'),
  sourceId: idRef.nullish(),
  leaseId: idRef.nullish(),
  unitId: idRef.nullish(),
  tenantId: idRef.nullish(),
  retentionClass: documentRetentionClassSchema.default('general'),
  visibility: documentVisibilitySchema.default('owner_only'),
  ab2801Phase: ab2801PhaseSchema.nullish(),
  capturedAt: isoDateString.nullish(),
};

/** A `tenant_visible` document must be bound to ≥1 of lease/unit/tenant (else unreachable). */
function tenantVisibleNeedsLink(v: {
  visibility: DocumentVisibility;
  leaseId?: string | null;
  unitId?: string | null;
  tenantId?: string | null;
}): boolean {
  return v.visibility !== 'tenant_visible' || Boolean(v.leaseId || v.unitId || v.tenantId);
}
const TENANT_VISIBLE_LINK_MSG = {
  message: 'tenant_visible requires at least one of leaseId, unitId, or tenantId',
  path: ['visibility'] as (string | number)[],
};

/**
 * Upload a new document (owner/staff). The bytes arrive base64-encoded in `contentBase64`
 * (JSON-friendly, portable across the DOM + Workers); the server enforces `MAX_UPLOAD_BYTES`
 * on the DECODED length and computes the sha256 itself (never trusts a client-supplied hash).
 */
export const documentUploadSchema = z
  .object({
    filename: filenameSchema,
    contentType: contentTypeSchema,
    contentBase64: contentBase64Schema,
    ...documentLinkFields,
  })
  .refine(tenantVisibleNeedsLink, TENANT_VISIBLE_LINK_MSG);
export type DocumentUploadInput = z.infer<typeof documentUploadSchema>;

/**
 * Register an ALREADY-uploaded R2 object as a documents row (owner/staff) — used by receipts /
 * notices / work-order photos that write their bytes to R2 by another path. The server
 * verifies the object exists (HEAD) before inserting; `sha256`/`sizeBytes` are supplied by the
 * caller and recorded as-is (the object is already sealed in R2).
 */
export const documentRegisterSchema = z
  .object({
    r2Key: z.string().trim().min(1).max(1024),
    filename: filenameSchema,
    contentType: contentTypeSchema,
    sha256: sha256HexSchema,
    sizeBytes: z.number().int().min(0),
    ...documentLinkFields,
  })
  .refine(tenantVisibleNeedsLink, TENANT_VISIBLE_LINK_MSG);
export type DocumentRegisterInput = z.infer<typeof documentRegisterSchema>;

/** Mint a short-lived signed content URL (owner/staff/tenant-own). Optional TTL override. */
export const documentSignedUrlSchema = z
  .object({
    /** Lifetime in seconds (default 300; clamped server-side). */
    ttlSeconds: z.number().int().min(30).max(3600).optional(),
  })
  .optional();
export type DocumentSignedUrlInput = z.infer<typeof documentSignedUrlSchema>;

/**
 * The executed-lease PDF upload (T4.2 back-fill; POST /api/leases/:id/document). Bytes arrive
 * base64 like the document upload; the produced row is `source_type='lease'`,
 * `retention_class='lease'`, and the link/visibility are set by the route (bound to the lease).
 */
export const leaseDocumentUploadSchema = z.object({
  filename: filenameSchema.default('lease.pdf'),
  contentType: contentTypeSchema.default('application/pdf'),
  contentBase64: contentBase64Schema,
  title: optShortText,
});
export type LeaseDocumentUploadInput = z.infer<typeof leaseDocumentUploadSchema>;

/* ------------------------------------------------------------------ *
 * EPIC 6 — Maintenance / work orders edge schemas.                   *
 * ------------------------------------------------------------------ */

export const workOrderCategorySchema = z.enum([
  'plumbing',
  'electrical',
  'hvac',
  'appliance',
  'structural',
  'pest',
  'landscaping',
  'safety',
  'general',
]);
export type WorkOrderCategory = z.infer<typeof workOrderCategorySchema>;

export const workOrderPrioritySchema = z.enum(['emergency', 'urgent', 'routine', 'low']);
export type WorkOrderPriority = z.infer<typeof workOrderPrioritySchema>;

export const workOrderStatusSchema = z.enum([
  'submitted',
  'triaged',
  'assigned',
  'scheduled',
  'resolved',
  'closed',
  'cancelled',
]);
export type WorkOrderStatus = z.infer<typeof workOrderStatusSchema>;

export const entryNoticePurposeSchema = z.enum(['repair', 'inspection', 'showing', 'emergency']);
export type EntryNoticePurpose = z.infer<typeof entryNoticePurposeSchema>;

/**
 * The TRUE legal basis for a <24h entry notice (review FIX 2). Required by the route (409
 * `short_notice_reason_required`) whenever the actual lead is short AND `overrideShortNotice` is
 * used — a bare admin boolean used to be sufficient, but the rendered notice affirmatively
 * claimed "with the tenant's prior consent" regardless of why notice was actually shortened.
 * `emergency` → §1954(e); `tenant_consented` → the ONLY case the notice states consent;
 * `other` → a neutral shortened-notice statement (never fabricates consent).
 */
export const shortNoticeReasonSchema = z.enum(['emergency', 'tenant_consented', 'other']);
export type ShortNoticeReason = z.infer<typeof shortNoticeReasonSchema>;

const optNote500 = z.string().trim().max(500).nullish();
const longText = z.string().trim().min(1).max(4000);

/**
 * A photo attached to a work-order submission (T6.1). Bytes arrive base64 (JSON-friendly). The
 * server hashes + size-caps them and stores each as a `documents` row (`source_type=work_order`).
 * `capturedAt` feeds the AB2801 photo-phase metadata.
 */
export const workOrderPhotoInputSchema = z.object({
  filename: filenameSchema,
  contentType: contentTypeSchema,
  contentBase64: contentBase64Schema,
  capturedAt: isoDateString.nullish(),
});
export type WorkOrderPhotoInput = z.infer<typeof workOrderPhotoInputSchema>;

/**
 * Tenant work-order submission (T6.1; POST /api/portal/work-orders). The submitting tenant + the
 * unit/lease are derived SERVER-SIDE from the tenant's own tenancy — `leaseId` is accepted only
 * to disambiguate a tenant with more than one active lease, and is verified to belong to the
 * caller (never trusted as an ownership grant). The unit is always derived from the chosen lease.
 */
export const workOrderSubmitSchema = z.object({
  /** Optional disambiguation when the tenant is on more than one active lease. */
  leaseId: idRef.nullish(),
  category: workOrderCategorySchema,
  title: shortText,
  description: longText,
  priority: workOrderPrioritySchema.default('routine'),
  permissionToEnter: z.boolean().default(false),
  availability: optNote500,
  photos: z.array(workOrderPhotoInputSchema).max(10).optional(),
})
  // Cap the AGGREGATE encoded size across all photos (assessment #9) — per-photo the base64 is
  // already bounded, but 10 × max would buffer ~349 MB and risk the isolate's memory limit. Bound
  // the sum to a single max-file body so a batch can't OOM the Worker.
  .superRefine((v, ctx) => {
    const total = (v.photos ?? []).reduce((n, p) => n + p.contentBase64.length, 0);
    if (total > MAX_CONTENT_BASE64_LENGTH) {
      ctx.addIssue({ code: 'custom', path: ['photos'], message: 'total photo upload is too large' });
    }
  });
export type WorkOrderSubmitInput = z.infer<typeof workOrderSubmitSchema>;

/**
 * Admin-raised work order (owner/staff; POST /api/work-orders). The unit is explicit (verified
 * in-org server-side); `leaseId` is optional and verified to belong to the unit's org.
 */
export const workOrderAdminCreateSchema = z.object({
  unitId: idRef,
  leaseId: idRef.nullish(),
  category: workOrderCategorySchema,
  title: shortText,
  description: longText,
  priority: workOrderPrioritySchema.default('routine'),
  permissionToEnter: z.boolean().default(false),
  availability: optNote500,
});
export type WorkOrderAdminCreateInput = z.infer<typeof workOrderAdminCreateSchema>;

/**
 * Generic status transition (PATCH /api/work-orders/:id; owner/staff). The target `scheduled`
 * is REFUSED here (409 use_schedule_endpoint) so the §1954 entry-notice gate can never be
 * bypassed — scheduling entry must go through POST /:id/schedule. The state machine
 * `applyTransition` validates the transition (else 409 invalid_transition).
 */
export const workOrderTransitionSchema = z.object({
  status: workOrderStatusSchema,
  note: optNote500,
});
export type WorkOrderTransitionInput = z.infer<typeof workOrderTransitionSchema>;

/**
 * Assign a work order (owner/staff; POST /:id/assign) → status `assigned`. Either a free-text
 * `assignedTo` (legacy) or a `contractorId` (0.25.0 — a `contractors` row; grants that contractor
 * access to the job) or both; with only `contractorId`, `assigned_to` records the company name.
 */
export const workOrderAssignSchema = z
  .object({
    assignedTo: optShortText,
    contractorId: idRef.nullish(),
    note: optNote500,
  })
  .refine((v) => Boolean(v.assignedTo) || Boolean(v.contractorId), {
    message: 'provide assignedTo or contractorId',
    path: ['assignedTo'],
  });
export type WorkOrderAssignInput = z.infer<typeof workOrderAssignSchema>;

/**
 * Schedule entry (T6.3; owner/staff; POST /:id/schedule) → status `scheduled` + the §1954
 * 24-hour entry-notice pipeline. `scheduledEntryStart`/`scheduledEntryEnd` are STRICT datetimes
 * (review FIX 1 — an offset-less value would make the ≥24h gate host-timezone-dependent).
 * `scheduledEntryStart` MUST lead by ≥ `entry_notice_hours` unless `overrideShortNotice` — and
 * MUST be strictly in the future (else 409 `entry_time_in_past`, review FIX 3 — checked on BOTH
 * the normal and override paths). When overridden, `shortNoticeReason` records the TRUE legal
 * basis (409 `short_notice_reason_required` if missing — review FIX 2); irrelevant/optional on a
 * normal ≥24h schedule.
 */
export const workOrderScheduleSchema = z.object({
  scheduledEntryStart: isoDateTimeStrict,
  scheduledEntryEnd: isoDateTimeStrict.nullish(),
  purpose: entryNoticePurposeSchema.default('repair'),
  /** <24h notice being served (recorded + audited on the entry notice). */
  overrideShortNotice: z.boolean().default(false),
  /** Required (409 if missing) whenever the actual lead is <24h and `overrideShortNotice` is used. */
  shortNoticeReason: shortNoticeReasonSchema.nullish(),
  assignedTo: optShortText,
  note: optNote500,
})
  // 0.25.0 security review L1: an entry END, when given, must be after the start.
  .refine((v) => !v.scheduledEntryEnd || Date.parse(v.scheduledEntryEnd) > Date.parse(v.scheduledEntryStart), {
    message: 'scheduledEntryEnd must be after scheduledEntryStart',
    path: ['scheduledEntryEnd'],
  });
export type WorkOrderScheduleInput = z.infer<typeof workOrderScheduleSchema>;

/** Resolve a work order (owner/staff; POST /:id/resolve) → status `resolved`. */
export const workOrderResolveSchema = z.object({
  resolutionNote: z.string().trim().max(4000).nullish(),
  resolutionCostCents: nonNegInt.nullish(),
  note: optNote500,
});
export type WorkOrderResolveInput = z.infer<typeof workOrderResolveSchema>;

/** Cancel a work order (owner/staff; POST /:id/cancel) → status `cancelled`. */
export const workOrderCancelSchema = z.object({
  cancelledReason: z.string().trim().max(500).nullish(),
  note: optNote500,
});
export type WorkOrderCancelInput = z.infer<typeof workOrderCancelSchema>;

/** Close a work order (owner/staff; POST /:id/close) → status `closed`. */
export const workOrderCloseSchema = z
  .object({ note: optNote500 })
  .optional();
export type WorkOrderCloseInput = z.infer<typeof workOrderCloseSchema>;

/**
 * Append a landlord-internal note (owner/staff; POST /:id/note) — NOT a status transition.
 * Written as a `work_order_status_history` row with `tenantVisible=false`, so the tenant portal
 * never sees it while owner/staff still do (the previously-dead `tenantVisible` filter in
 * `loadDetail` now has a real producer).
 */
export const workOrderNoteSchema = z.object({
  note: z.string().trim().min(1).max(500),
});
export type WorkOrderNoteInput = z.infer<typeof workOrderNoteSchema>;

/**
 * Dispatch a work order to the maintenance contractor (owner/staff; POST /:id/dispatch). Emails
 * the job details out-of-band — NOT a status transition. With no `toEmail`, the server fans the
 * email out to the org's configured contractor address list (409 `no_contractor_configured` when
 * that list is empty); `toEmail` overrides for a one-off recipient. `markAssigned` optionally moves
 * a `triaged`/`assigned`-eligible order to `assigned` (recording the contractor as `assigned_to`)
 * via the state machine; it is a no-op when the current status can't transition to `assigned`.
 */
export const workOrderDispatchSchema = z.object({
  /** One-off recipient override; when omitted the org contractor address list is used. */
  toEmail: emailSchema.nullish(),
  /**
   * 0.25.0 — target a specific `contractors` row: the email fans out to THAT contractor's address
   * list (instead of the org default), and `markAssigned` also sets `assigned_contractor_id`.
   * Mutually exclusive with `toEmail` (400).
   */
  contractorId: idRef.nullish(),
  note: optNote500,
  markAssigned: z.boolean().default(false),
});
export type WorkOrderDispatchInput = z.infer<typeof workOrderDispatchSchema>;

/**
 * Set the org's primary maintenance contractor (owner-only + MFA; PUT /api/org/contractor). One
 * contractor by design; `emails` is the full replacement dispatch list (0–10 addresses — a job is
 * emailed to EVERY one, so "send to both" is just two entries). An empty list clears the default
 * (dispatch then requires an explicit `toEmail`). `name`/`phone` are optional metadata.
 */
export const contractorUpdateSchema = z.object({
  name: optShortText,
  emails: z.array(emailSchema).max(10),
  phone: z.string().trim().max(40).nullish(),
});
export type ContractorUpdateInput = z.infer<typeof contractorUpdateSchema>;

/**
 * Tax export filter (owner/staff; GET /api/work-orders/export.xlsx). Selects COMPLETED work orders
 * by completion date (closed_at ?? resolved_at). `year` bounds to a single tax year; `from`/`to`
 * (ISO dates) give an explicit range and override `year` when present. `status` restricts to
 * `resolved` or `closed`, or `all` (both — the default). Every field is optional; no filter = every
 * completed order.
 */
export const workOrderExportQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  from: z.string().trim().min(1).max(40).optional(),
  to: z.string().trim().min(1).max(40).optional(),
  status: z.enum(['resolved', 'closed', 'all']).default('all'),
});
export type WorkOrderExportQuery = z.infer<typeof workOrderExportQuerySchema>;

/* ------------------------------------------------------------------ *
 * EPIC 8 — Messaging edge schemas (threads / messages / search).     *
 * ------------------------------------------------------------------ */

const messageBody = z.string().trim().min(1, { message: 'message body is required' }).max(8000);

/**
 * Create a thread (T8.1; owner/staff/tenant). Three shapes:
 *   - `leaseId` (preferred; the unit is derived from it server-side) — a lease conversation,
 *   - `unitId` — a unit conversation,
 *   - NEITHER — a DIRECT thread to org management (a tenant with no lease/unit context; the server
 *     restricts this shape to a TENANT caller — owner/staff must target a lease/unit).
 * Participants are always derived SERVER-SIDE (never client-supplied): a lease/unit thread from the
 * lease's tenants + the administering owner/staff + the creator; a direct thread from the creating
 * tenant + the org's active owner/staff. An optional first `body` posts the opening message
 * atomically. The lease-less path is what guarantees a tenant can always reach management.
 */
export const threadCreateSchema = z.object({
  leaseId: idRef.nullish(),
  unitId: idRef.nullish(),
  subject: shortText,
  /** Optional opening message posted with the thread. */
  body: messageBody.optional(),
});
export type ThreadCreateInput = z.infer<typeof threadCreateSchema>;

/**
 * Post a message to a thread (T8.1; participant-only). `attachmentDocumentIds` reference existing
 * `documents` the SENDER can access (verified server-side; a doc the sender can't access → 404).
 * The body is kept + full-text searchable but NEVER written into an audit diff (PII discipline).
 */
export const messagePostSchema = z.object({
  body: messageBody,
  attachmentDocumentIds: z.array(idRef).max(10).optional(),
});
export type MessagePostInput = z.infer<typeof messagePostSchema>;

/** GET /api/messages/search?q= — free-text query over message bodies (org + participant scoped). */
export const messageSearchQuerySchema = z.object({
  q: z.string().trim().min(1, { message: 'q is required' }).max(200),
});
export type MessageSearchQuery = z.infer<typeof messageSearchQuerySchema>;

/* ------------------------------------------------------------------ *
 * EPIC 9 — Payment methods edge schemas (§1947.3 accepted-methods).  *
 * ------------------------------------------------------------------ */

/** The accepted-payment-method identifiers (blueprint §0009 / resolution #14). */
export const paymentMethodMethodSchema = z.enum([
  'check',
  'money_order',
  'cashier_check',
  'ach',
  'card',
  'portal_eft',
  // Electronic but MANUAL (peer-to-peer, off-portal) — listed as an accepted method; the landlord
  // settles it directly. Not a §1947.3 fee-free non-EFT path (that stays check/money_order/cash).
  'venmo',
]);
export type PaymentMethodMethod = z.infer<typeof paymentMethodMethodSchema>;

/**
 * Create an accepted payment method (owner/staff). The §1947.3 SERVER INVARIANT — the enabled set
 * must always keep ≥1 `isEft=false, feeCents=0` method — is enforced in the handler (422
 * `non_eft_path_required`), NOT here, so this validates SHAPE only. UNIQUE(org, method) → 409.
 */
export const paymentMethodCreateSchema = z.object({
  method: paymentMethodMethodSchema,
  label: optShortText,
  isEft: z.boolean().default(false),
  enabled: z.boolean().default(true),
  feeCents: nonNegInt.default(0),
  instructions: z.string().trim().max(2000).nullish(),
  remittanceTo: z.string().trim().max(200).nullish(),
  sortOrder: z.number().int().min(0).max(1000).default(0),
});
export type PaymentMethodCreateInput = z.infer<typeof paymentMethodCreateSchema>;

/** Update an accepted payment method (owner/staff) — all fields optional. `method` is fixed. */
export const paymentMethodUpdateSchema = z
  .object({
    label: optShortText,
    isEft: z.boolean().optional(),
    enabled: z.boolean().optional(),
    feeCents: nonNegInt.optional(),
    instructions: z.string().trim().max(2000).nullish(),
    remittanceTo: z.string().trim().max(200).nullish(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type PaymentMethodUpdateInput = z.infer<typeof paymentMethodUpdateSchema>;

/** Owner eligibility-flag update (T3.1 — the only owner mutation in Phase 1). */
export const ownerUpdateSchema = z
  .object({
    name: shortText.optional(),
    email: emailSchema.optional(),
    isNaturalPerson: z.boolean().optional(),
    smallLandlordEligible: z.boolean().optional(),
    sixteenPlusUnits: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type OwnerUpdateInput = z.infer<typeof ownerUpdateSchema>;

/* ------------------------------------------------------------------ *
 * Online payments — tenant ACH rent (Stripe).                        *
 * ------------------------------------------------------------------ */

/**
 * Create a one-time ACH rent PaymentIntent (tenant portal; POST /api/portal/pay/intent). The
 * server derives the lease + org from the CALLER'S OWN tenancy — only `amountCents` is
 * client-supplied. Capped at $100,000 (far above any single rent) so a fat-finger or a hostile
 * client can't create a runaway charge; the positivity + integer rules match the ledger's.
 */
export const payIntentSchema = z.object({
  amountCents: z
    .number()
    .int()
    .positive({ message: 'amountCents must be a positive integer' })
    .max(100_000_00, { message: 'amountCents exceeds the maximum' }),
});
export type PayIntentInput = z.infer<typeof payIntentSchema>;

/* ------------------------------------------------------------------ *
 * 0.24.0 — Move-out report + § 1950.5 deposit disposition schemas.   *
 * ------------------------------------------------------------------ */

export const moveOutCategorySchema = z.enum([
  'unpaid_rent',
  'cleaning',
  'repair',
  'key_replacement',
  'utilities',
  'other',
]);
export type MoveOutCategoryInput = z.infer<typeof moveOutCategorySchema>;

export const moveOutDocumentRoleSchema = z.enum([
  'move_in_photo',
  'move_out_photo',
  'post_repair_photo',
  'receipt',
  'invoice',
  'lease',
  'other',
]);
export type MoveOutDocumentRoleInput = z.infer<typeof moveOutDocumentRoleSchema>;

export const moveOutAddressKindSchema = z.enum(['forwarding', 'last_known']);
export type MoveOutAddressKind = z.infer<typeof moveOutAddressKindSchema>;

/**
 * A strict `YYYY-MM-DD` calendar date. The § 1950.5 clock counts calendar days, so the
 * possession date is date-only — no time-of-day, no host-timezone ambiguity.
 */
export const calendarDateSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'must be a YYYY-MM-DD date' })
  .refine(
    (v) => {
      const [y, m, d] = v.split('-').map(Number) as [number, number, number];
      const dt = new Date(Date.UTC(y, m - 1, d));
      return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
    },
    { message: 'must be a real calendar date' },
  );

/** A US mailing address (encrypted at rest as one blob on the recipient row). */
export const mailingAddressSchema = z.object({
  line1: shortText,
  line2: optShortText,
  city: z.string().trim().min(1).max(100),
  state: z.string().trim().min(2).max(40),
  postalCode: z.string().trim().min(3).max(20),
});
export type MailingAddressInput = z.infer<typeof mailingAddressSchema>;

/** Start a move-out report for a lease. `depositHeldCents` defaults to the lease deposit. */
export const moveOutReportCreateSchema = z.object({
  leaseId: idRef,
  possessionReturnedDate: calendarDateSchema.nullish(),
  depositHeldCents: nonNegInt.max(100_000_000).optional(),
  ownerNotes: optNotes,
});
export type MoveOutReportCreateInput = z.infer<typeof moveOutReportCreateSchema>;

/** Edit a DRAFT report. `statementDueDate` is never client-supplied (server-computed). */
export const moveOutReportUpdateSchema = z
  .object({
    possessionReturnedDate: calendarDateSchema.nullish(),
    depositHeldCents: nonNegInt.max(100_000_000).optional(),
    ownerNotes: optNotes,
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type MoveOutReportUpdateInput = z.infer<typeof moveOutReportUpdateSchema>;

/** Add a statement recipient (a tenant on the lease) + their mailing address. */
export const moveOutRecipientAddSchema = z.object({
  tenantId: idRef,
  addressKind: moveOutAddressKindSchema.default('forwarding'),
  address: mailingAddressSchema.nullish(),
});
export type MoveOutRecipientAddInput = z.infer<typeof moveOutRecipientAddSchema>;

export const moveOutRecipientUpdateSchema = z
  .object({
    addressKind: moveOutAddressKindSchema.optional(),
    address: mailingAddressSchema.nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type MoveOutRecipientUpdateInput = z.infer<typeof moveOutRecipientUpdateSchema>;

const deductionAmount = z
  .number()
  .int()
  .positive({ message: 'amountCents must be greater than zero' })
  .max(100_000_000);
const laborMinutes = z.number().int().positive().max(100_000);
const laborRate = z.number().int().positive().max(10_000_000);

/** Owner-labor time and rate are set together or not at all. */
function laborPaired(v: { ownerLaborMinutes?: number | null; ownerLaborRateCents?: number | null }) {
  return (v.ownerLaborMinutes == null) === (v.ownerLaborRateCents == null);
}
const LABOR_PAIR_MSG = {
  message: 'ownerLaborMinutes and ownerLaborRateCents must be set together',
  path: ['ownerLaborMinutes'] as (string | number)[],
};

/** Add an itemized deduction to a DRAFT report. */
export const moveOutDeductionCreateSchema = z
  .object({
    category: moveOutCategorySchema,
    description: z.string().trim().min(1).max(1000),
    amountCents: deductionAmount,
    isEstimate: z.boolean().default(false),
    vendorInfo: z.string().trim().max(500).nullish(),
    ownerLaborMinutes: laborMinutes.nullish(),
    ownerLaborRateCents: laborRate.nullish(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
  })
  .refine(laborPaired, LABOR_PAIR_MSG);
export type MoveOutDeductionCreateInput = z.infer<typeof moveOutDeductionCreateSchema>;

/** Edit a deduction (the labor pairing is re-checked server-side on the RESULTING values). */
export const moveOutDeductionUpdateSchema = z
  .object({
    category: moveOutCategorySchema.optional(),
    description: z.string().trim().min(1).max(1000).optional(),
    amountCents: deductionAmount.optional(),
    isEstimate: z.boolean().optional(),
    vendorInfo: z.string().trim().max(500).nullish(),
    ownerLaborMinutes: laborMinutes.nullish(),
    ownerLaborRateCents: laborRate.nullish(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type MoveOutDeductionUpdateInput = z.infer<typeof moveOutDeductionUpdateSchema>;

/**
 * Attach evidence to a DRAFT report: EITHER an existing in-org `documentId`, OR a new `upload`
 * (base64 bytes, stored as a `documents` row with source_type `move_out`, linked to the lease,
 * owner_only). Photos should arrive as JPEG/PNG — the SPA converts iPhone HEIC before upload.
 *
 * Renditions (0.24.0): for an image the SPA sends `upload` = a ≤2048 px JPEG RENDITION and
 * `original` = the untouched original file (EXIF intact). The server stores BOTH; the rendition
 * gets `derived_from_document_id` → the original, and the report attachment points at the
 * RENDITION (the statement PDF embeds it). `original` is only valid together with `upload`.
 */
export const moveOutDocumentAttachSchema = z
  .object({
    role: moveOutDocumentRoleSchema,
    deductionId: idRef.nullish(),
    caption: z.string().trim().max(500).nullish(),
    room: z.string().trim().max(100).nullish(),
    documentId: idRef.optional(),
    upload: z
      .object({
        filename: filenameSchema,
        contentType: contentTypeSchema,
        contentBase64: contentBase64Schema,
        title: optShortText,
        capturedAt: isoDateString.nullish(),
      })
      .optional(),
    original: z
      .object({
        filename: filenameSchema,
        contentType: contentTypeSchema,
        contentBase64: contentBase64Schema,
      })
      .optional(),
  })
  .refine((v) => Boolean(v.documentId) !== Boolean(v.upload), {
    message: 'provide exactly one of documentId or upload',
    path: ['documentId'],
  })
  .refine((v) => !v.original || Boolean(v.upload), {
    message: 'original is only valid together with upload (the rendition)',
    path: ['original'],
  });
export type MoveOutDocumentAttachInput = z.infer<typeof moveOutDocumentAttachSchema>;

export const moveOutDocumentUpdateSchema = z
  .object({
    role: moveOutDocumentRoleSchema.optional(),
    deductionId: idRef.nullish(),
    caption: z.string().trim().max(500).nullish(),
    room: z.string().trim().max(100).nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type MoveOutDocumentUpdateInput = z.infer<typeof moveOutDocumentUpdateSchema>;

/** Share (or un-share) the FINALIZED statement document with the lease's tenants. */
export const moveOutStatementVisibilitySchema = z.object({
  visibility: documentVisibilitySchema,
});
export type MoveOutStatementVisibilityInput = z.infer<typeof moveOutStatementVisibilitySchema>;

/**
 * 0.28.1 — REOPEN a finalized report for amendment (owner only). The reason is recorded on the
 * superseded revision and in the audit log.
 */
export const moveOutReopenSchema = z.object({
  reason: z
    .string({ message: 'a reason is required' })
    .trim()
    .min(10, { message: 'give a reason of at least 10 characters' })
    .max(500, { message: 'the reason is limited to 500 characters' }),
});
export type MoveOutReopenInput = z.infer<typeof moveOutReopenSchema>;

/* ---- 0.27.0 — owner signature profile + sign / send the finalized statement ---- */

const noControlChars = (s: string) => ![...s].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f);

/** The owner's typed signature (printed on every document they sign). */
export const ownerSignatureUpdateSchema = z.object({
  // Bounded so the signature page (name, "/s/" line, record block) always fits ONE appended page.
  signerName: z.string().trim().min(1).max(80).refine(noControlChars, { message: 'must not contain control characters' }),
  signerTitle: z.string().trim().min(1).max(120).refine(noControlChars, { message: 'must not contain control characters' }),
});
export type OwnerSignatureUpdateInput = z.infer<typeof ownerSignatureUpdateSchema>;

/**
 * The owner's handwritten signature image — PNG or JPEG, ≤ 1 MB (the server re-checks the decoded
 * size and sniffs the magic bytes; the base64 bound here only caps the pre-decode buffer).
 */
export const ownerSignatureImageSchema = z.object({
  filename: filenameSchema,
  contentType: z.enum(['image/png', 'image/jpeg']),
  contentBase64: z.string().min(1, { message: 'contentBase64 is required' }).max(1_400_000, { message: 'image too large (max 1 MB)' }),
});
export type OwnerSignatureImageInput = z.infer<typeof ownerSignatureImageSchema>;

/** Sign the finalized statement. `force` makes a NEW signed copy and supersedes the current one. */
export const moveOutSignSchema = z.object({ force: z.boolean().optional() });
export type MoveOutSignInput = z.infer<typeof moveOutSignSchema>;

/** The ONE recipient field editable after finalize: the delivery email (null clears it). */
export const moveOutRecipientDeliverySchema = z.object({ email: emailSchema.max(254).nullable() });
export type MoveOutRecipientDeliveryInput = z.infer<typeof moveOutRecipientDeliverySchema>;

/**
 * Email the signed statement. One email per recipient. `acknowledgeEmailDelivery` must be `true`
 * (the server answers 422 otherwise) — § 1950.5(g)(1) calls for personal delivery or first-class
 * mail unless the tenant agreed to email. `resend` is required to email a recipient who already
 * has a `sent` delivery of the current signed copy.
 */
export const moveOutSendSchema = z.object({
  recipientIds: z.array(idRef).min(1).max(20).optional(),
  subject: z.string().trim().min(1).max(200).refine(noControlChars, { message: 'must not contain control characters' }).optional(),
  coverNote: z.string().max(4000, { message: 'cover note is limited to 4,000 characters' }).default(''),
  acknowledgeEmailDelivery: z.boolean().optional(),
  resend: z.boolean().optional(),
  /**
   * Send ONE test copy of the real email INSTEAD of emailing the recipients — to the caller's own
   * login address (`self`) or the configured Reply-To mailbox (`reply_to`); never a free address.
   * Nothing is recorded in the delivery register and no acknowledgement is needed.
   */
  testTo: z.enum(['self', 'reply_to']).optional(),
  /**
   * 0.28.3 — blind-copy these addresses on EACH recipient's email (and on a `testTo` copy). At most
   * 5 entries (`MOVE_OUT_BCC_MAX`), each a valid email (trimmed + lower-cased), de-duplicated. The
   * server drops, per email, an entry equal to the address that email is already going to.
   */
  bcc: z
    .array(emailSchema.max(254))
    .max(5, { message: 'at most 5 blind-copy addresses' })
    .transform((v) => [...new Set(v)])
    .optional(),
  /**
   * 0.28.4 — VISIBLE copies: the same rules as `bcc` (at most 5 — `MOVE_OUT_CC_MAX`), but the
   * recipient sees these addresses. An address listed as both cc and bcc is sent as a cc only.
   */
  cc: z
    .array(emailSchema.max(254))
    .max(5, { message: 'at most 5 copy (cc) addresses' })
    .transform((v) => [...new Set(v)])
    .optional(),
});
export type MoveOutSendInput = z.input<typeof moveOutSendSchema>;


/* ------------------------------------------------------------------ *
 * 0.25.0 — Contractor role edge schemas.                              *
 * ------------------------------------------------------------------ */

const contractorEmails = z.array(emailSchema).max(10);
const optTrade = z.string().trim().max(100).nullish();
const optLicense = z.string().trim().max(60).nullish();

/** Create a contractor in the org directory (owner/staff + MFA; POST /api/contractors). */
export const contractorProfileCreateSchema = z.object({
  companyName: shortText,
  contactName: optShortText,
  /** Assignment emails fan out to every address; the login address is chosen at invite. */
  emails: contractorEmails.default([]),
  phone: optPhone,
  trade: optTrade,
  licenseNo: optLicense,
});
export type ContractorProfileCreateInput = z.infer<typeof contractorProfileCreateSchema>;

/** Edit a contractor (owner/staff + MFA; PATCH /api/contractors/:id). `emails` fully replaces. */
export const contractorProfileUpdateSchema = z
  .object({
    companyName: shortText.optional(),
    contactName: optShortText,
    emails: contractorEmails.optional(),
    phone: optPhone,
    trade: optTrade,
    licenseNo: optLicense,
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });
export type ContractorProfileUpdateInput = z.infer<typeof contractorProfileUpdateSchema>;

/**
 * Invite a contractor to sign in (owner/staff + MFA; POST /api/contractors/:id/invite). `email`
 * picks the login address and MUST be one of the contractor's `emails` (default: the first).
 */
export const contractorInviteSchema = z
  .object({ email: emailSchema.nullish() })
  .optional();
export type ContractorInviteInput = z.infer<typeof contractorInviteSchema>;

/**
 * Set / clear the contractor on a work order WITHOUT a status transition (owner/staff + MFA;
 * PUT /api/work-orders/:id/contractor). `null` unassigns — the contractor loses access at once.
 */
export const workOrderSetContractorSchema = z.object({
  contractorId: idRef.nullable(),
  note: optNote500,
});
export type WorkOrderSetContractorInput = z.infer<typeof workOrderSetContractorSchema>;

/** Contractor job list filter (GET /api/contractor/work-orders?status=). */
export const contractorJobListQuerySchema = z.object({
  status: z.enum(['open', 'closed', 'all']).default('open'),
});
export type ContractorJobListQuery = z.infer<typeof contractorJobListQuerySchema>;

/**
 * Contractor status change (POST /api/contractor/work-orders/:id/status). A contractor may ONLY
 * mark the work `resolved` (from `scheduled`). They can NEVER schedule entry (operator decision —
 * they propose a window via /schedule-proposals and the owner confirms), close, cancel, triage or
 * reassign.
 */
export const contractorStatusSchema = z.object({
  status: z.literal('resolved'),
  resolutionNote: z.string().trim().max(4000).nullish(),
  note: optNote500,
});
export type ContractorStatusInput = z.infer<typeof contractorStatusSchema>;

/** Contractor progress/completion photos (POST /api/contractor/work-orders/:id/photos). */
export const contractorPhotosSchema = z
  .object({ photos: z.array(workOrderPhotoInputSchema).min(1).max(10) })
  .superRefine((v, ctx) => {
    const total = v.photos.reduce((n, p) => n + p.contentBase64.length, 0);
    if (total > MAX_CONTENT_BASE64_LENGTH) {
      ctx.addIssue({ code: 'custom', path: ['photos'], message: 'total photo upload is too large' });
    }
  });
export type ContractorPhotosInput = z.infer<typeof contractorPhotosSchema>;

/** A cost amount: positive integer cents, capped at $1,000,000 (fat-finger guard). */
const costCents = z.number().int().min(1).max(100_000_000);

/**
 * Contractor submits a cost (POST /api/contractor/work-orders/:id/costs). NOT final — the owner
 * approves/rejects. `invoice` (photo/PDF) is stored as an owner_only `financial` document.
 */
export const workOrderCostSubmitSchema = z.object({
  amountCents: costCents,
  description: z.string().trim().min(1).max(1000),
  invoice: z
    .object({
      filename: filenameSchema,
      contentType: contentTypeSchema,
      contentBase64: contentBase64Schema,
    })
    .nullish(),
});
export type WorkOrderCostSubmitInput = z.infer<typeof workOrderCostSubmitSchema>;

/** Contractor edits a STILL-submitted cost (PATCH /api/contractor/costs/:costId). */
export const workOrderCostUpdateSchema = z
  .object({
    amountCents: costCents.optional(),
    description: z.string().trim().min(1).max(1000).optional(),
  })
  .refine((v) => v.amountCents !== undefined || v.description !== undefined, {
    message: 'no fields to update',
  });
export type WorkOrderCostUpdateInput = z.infer<typeof workOrderCostUpdateSchema>;

/**
 * Owner approves / rejects a cost (POST /api/work-order-costs/:id/approve|reject). Security review
 * M2: `expectedAmountCents` (the amount the owner SAW) is REQUIRED and is part of the review's
 * compare-and-set — if the contractor edited the amount in between, the review is refused (409
 * `cost_changed`) instead of approving a number the owner never saw.
 */
export const workOrderCostReviewSchema = z.object({
  expectedAmountCents: z.number().int().min(1),
  /** Follow-up review N8: the cost's `updatedAt` the owner saw — also part of the CAS (409 cost_changed). */
  expectedUpdatedAt: z.string().trim().min(1).max(40),
  reviewNote: z.string().trim().max(500).nullish(),
});
export type WorkOrderCostReviewInput = z.infer<typeof workOrderCostReviewSchema>;

/** Owner/staff cost list filter (GET /api/work-order-costs?status=). */
export const workOrderCostListQuerySchema = z.object({
  status: z.enum(['submitted', 'approved', 'rejected', 'all']).default('submitted'),
});
export type WorkOrderCostListQuery = z.infer<typeof workOrderCostListQuerySchema>;


/* ------------------------------------------------------------------ *
 * 0.25.0 — Schedule proposals (contractor proposes, owner confirms)   *
 * + additive job cost (manual + approved contractor costs).          *
 * ------------------------------------------------------------------ */

/** Contractor proposes an entry window (POST /api/contractor/work-orders/:id/schedule-proposals). */
export const contractorScheduleProposalSchema = z
  .object({
    windowStart: isoDateTimeStrict,
    windowEnd: isoDateTimeStrict,
    /** Note for the OWNER (never shown to the tenant). */
    note: optNote500,
  })
  .refine((v) => Date.parse(v.windowEnd) > Date.parse(v.windowStart), {
    message: 'windowEnd must be after windowStart',
    path: ['windowEnd'],
  });
export type ContractorScheduleProposalInput = z.infer<typeof contractorScheduleProposalSchema>;

export const scheduleProposalStatusSchema = z.enum(['proposed', 'confirmed', 'declined', 'withdrawn', 'superseded']);

/** Owner/staff proposal list filter (GET /api/schedule-proposals?status=&workOrderId=). */
export const scheduleProposalListQuerySchema = z.object({
  status: z.union([scheduleProposalStatusSchema, z.literal('all')]).default('proposed'),
  workOrderId: idRef.optional(),
});
export type ScheduleProposalListQuery = z.infer<typeof scheduleProposalListQuerySchema>;

/** Owner confirms / declines a proposal (POST /api/schedule-proposals/:id/confirm|decline). */
export const scheduleProposalReviewSchema = z
  .object({ reviewNote: z.string().trim().max(500).nullish() })
  .optional();
export type ScheduleProposalReviewInput = z.infer<typeof scheduleProposalReviewSchema>;

/**
 * Set the owner/staff MANUAL cost of a job (PUT /api/work-orders/:id/manual-cost). The job's total
 * (`resolutionCostCents`) is then recomputed = manual + approved contractor costs. `null` clears it.
 */
export const workOrderManualCostSchema = z.object({
  manualCostCents: nonNegInt.nullable(),
});
export type WorkOrderManualCostInput = z.infer<typeof workOrderManualCostSchema>;
